import { useEffect } from 'react';
// ============================================================
// PRODUCT CATALOG — single source of truth for tracking values
// All prices in US Dollars (USD).
// ============================================================
export type ProductKey = 'megapack';
export const PRODUCTS: Record<ProductKey, {
  id: string;
  name: string;
  value: number;
  currency: 'USD';
  checkoutUrl: string;
}> = {
  megapack: {
    id: 'megapack-colorimetria-lista',
    name: 'MEGAPACK COLORIMETRÍA LISTA',
    value: 21,
    currency: 'USD',
    checkoutUrl: 'https://megapack-colorimetria.impultienda.ar/checkout'
  }
};
const PIXEL_ID = '1331687825713998';
const PIXEL_SCRIPT_ID = 'fb-pixel-script';
// Window-level guard: survives React re-mounts, StrictMode double effects and
// hot reloads, so init/PageView/ViewContent can only ever fire once per load.
const INIT_FLAG = '__mpColorimetriaPixelReady';
// Ignore repeated CTA clicks within this window (double taps, rage clicks)
const CHECKOUT_THROTTLE = 1500;
let lastCheckoutEventAt = 0;
// Safely access fbq if available
const getFbq = (): ((...args: any[]) => void) | null => {
  if (typeof window === 'undefined') return null;
  const w = window as any;
  return typeof w.fbq === 'function' ? w.fbq : null;
};
const buildPayload = (productKey: ProductKey, overrideValue?: number) => {
  const p = PRODUCTS[productKey];
  const value = typeof overrideValue === 'number' ? overrideValue : p.value;
  return {
    value,
    currency: p.currency,
    content_ids: [p.id],
    content_name: p.name,
    content_type: 'product',
    contents: [{
      id: p.id,
      quantity: 1,
      item_price: value
    }],
    num_items: 1
  };
};
// Loads the real pixel script. Safe to call many times.
const loadPixelScript = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(PIXEL_SCRIPT_ID)) return;
  const script = document.createElement('script');
  script.id = PIXEL_SCRIPT_ID;
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
};
// ============================================================
// Tracking helpers — use these from anywhere in the app
// ============================================================
export const trackViewContent = (productKey: ProductKey) => {
  const fbq = getFbq();
  if (!fbq) return;
  fbq('track', 'ViewContent', buildPayload(productKey));
};
export const trackInitiateCheckout = (productKey: ProductKey) => {
  const fbq = getFbq();
  if (!fbq) return;
  const now = Date.now();
  if (now - lastCheckoutEventAt < CHECKOUT_THROTTLE) return;
  lastCheckoutEventAt = now;
  // Make sure the transport is on its way before the browser leaves the page.
  loadPixelScript();
  fbq('track', 'InitiateCheckout', buildPayload(productKey));
};
export const trackPurchase = (productKey: ProductKey, overrideValue?: number, eventID?: string) => {
  const fbq = getFbq();
  if (!fbq) return;
  fbq('track', 'Purchase', buildPayload(productKey, overrideValue), eventID ? {
    eventID
  } : undefined);
};
// ============================================================
// MetaPixel component — initializes the pixel and handles
// the Purchase return URL (?purchase=1[&value=...])
// ============================================================
export function MetaPixel() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const w = window as any;
    if (w[INIT_FLAG]) return;
    w[INIT_FLAG] = true;

    // 1) Set up the fbq queue immediately so no event can be missed, even
    //    before the network script has landed.
    if (!w.fbq) {
      const n: any = w.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
    }

    // 2) Standard events for this landing page.
    w.fbq('init', PIXEL_ID);
    w.fbq('track', 'PageView');
    trackViewContent('megapack');

    // 3) Load the transport without competing with the first paint: whenever
    //    the browser goes idle, or as soon as the visitor interacts.
    const interactionEvents: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'scroll'];
    const onFirstInteraction = () => {
      loadPixelScript();
      interactionEvents.forEach(evt => window.removeEventListener(evt, onFirstInteraction));
    };
    interactionEvents.forEach(evt => window.addEventListener(evt, onFirstInteraction, {
      once: true,
      passive: true
    }));
    let idleTimer: number | undefined;
    if ('requestIdleCallback' in w) {
      w.requestIdleCallback(loadPixelScript, {
        timeout: 2000
      });
    } else {
      idleTimer = window.setTimeout(loadPixelScript, 1200);
    }

    // 4) Purchase on return from the checkout:
    //    https://landing.com/?purchase=1[&value=21]
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('purchase')) {
        const valueParam = params.get('value');
        const parsedValue = valueParam ? Number(valueParam) : NaN;
        const overrideValue = Number.isFinite(parsedValue) ? parsedValue : undefined;
        // Stable id per order (or per return visit) so Meta de-duplicates.
        const eventID = `purchase-${params.get('order') || params.get('order_id') || Date.now()}`;
        loadPixelScript();
        trackPurchase('megapack', overrideValue, eventID);
        // Clean the query string so a refresh can't double-fire
        const url = new URL(window.location.href);
        url.searchParams.delete('purchase');
        url.searchParams.delete('value');
        window.history.replaceState({}, '', url.toString());
      }
    } catch {
      // Tracking must never break the landing page.
    }
    return () => {
      if (idleTimer) window.clearTimeout(idleTimer);
      interactionEvents.forEach(evt => window.removeEventListener(evt, onFirstInteraction));
    };
  }, []);
  return null;
}